import { Bot, PlayCircle, Network, X, BrainCircuit, Settings, BarChart3, Terminal, MessageSquare, Mail, CreditCard, LayoutGrid } from "lucide-react";
import { SuperAdminOrgSwitcher } from "./SuperAdminOrgSwitcher";
import { OrgSwitcherHint } from "./OrgSwitcherHint";
import { cn } from "@/infrastructure/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useOrganization, useOrganizationDetails } from "@/shared/hooks/api/useOrganization";
import { Separator } from "@/shared/components/ui/separator";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useSidebarSubscription } from "@/shared/hooks/api/useSidebarSubscription";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useViewScope, type ViewScope } from "@/shared/hooks/use-view-scope";
import { useEffect, useState, useRef, useMemo, type ComponentType } from "react";

const FALLBACK_ORG = {
  name: "Organização",
  logo_url: ""
};

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  icon: ComponentType<{ className?: string }>;
  label: string;
  href: string;
  scopes: ViewScope[];
  tourId?: string;
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { data: userProfile, isLoading: isLoadingProfile, refetch: refetchProfile } = useOrganization();
  const { data: organizationDetails, refetch: refetchOrgDetails } = useOrganizationDetails(userProfile?.organization_id);

  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { selectedOrganization, organizations } = useSuperAdminOrg();
  const { planName, subStatus, statusLabel, statusColor } = useSidebarSubscription();
  const { scope } = useViewScope();
  const isUserOnly = !isAdmin && !isSuperAdmin;

  // O card vira seletor quando há para onde ir (AB#84509): superadmin navega a
  // plataforma inteira, e qualquer usuário com mais de um vínculo alterna entre as
  // suas. Convite pendente NÃO abre o seletor — ele vive no sino de notificações.
  const canSwitchOrganization = isSuperAdmin || organizations.length > 1;
  const [orgName, setOrgName] = useState<string>("");
  const [orgLogo, setOrgLogo] = useState<string>("");
  const [logoUpdateCounter, setLogoUpdateCounter] = useState<number>(0);
  const dataLoadedRef = useRef<boolean>(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!isAuthenticated || !user || userProfile || !isLoadingProfile) {
      return;
    }

    let retryCount = 0;
    const MAX_RETRIES = 3;

    const attemptProfileFetch = async () => {
      retryCount++;

      try {
        queryClient.removeQueries({ queryKey: ['current-user-profile'] });
        const result = await refetchProfile();

        if (result.data) {
          return;
        }
      } catch (error) {
        console.warn(`[Sidebar] Tentativa ${retryCount}/${MAX_RETRIES} falhou:`, error);
      }

      if (retryCount < MAX_RETRIES) {
        retryTimeoutRef.current = setTimeout(attemptProfileFetch, 500 * retryCount);
      }
    };

    retryTimeoutRef.current = setTimeout(attemptProfileFetch, 300);

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isAuthenticated, user, userProfile, isLoadingProfile, queryClient, refetchProfile]);

  useEffect(() => {
    if (userProfile?.organization_id && !organizationDetails) {
      refetchOrgDetails();
    }
  }, [userProfile, organizationDetails, refetchOrgDetails]);

  // O card exibe a organização ATIVA, para qualquer papel (AB#84509). Antes o
  // caminho não-superadmin lia a organização do perfil e só uma vez — com
  // multi-organização isso deixaria o card preso no nome da organização de origem
  // depois de trocar.
  useEffect(() => {
    if (selectedOrganization) {
      setOrgName(selectedOrganization.name || "");
      setOrgLogo(selectedOrganization.logo_url || "");
      setLogoUpdateCounter(prev => prev + 1);
      dataLoadedRef.current = true;
      return;
    }
    // Superadmin em escopo de plataforma não tem organização — o card fica vazio de
    // propósito. Os demais caem aqui só enquanto a lista não resolveu.
    if (isSuperAdmin) {
      setOrgName("");
      setOrgLogo("");
      return;
    }
    if (organizationDetails && !dataLoadedRef.current) {
      setOrgName(organizationDetails.name || "");
      setOrgLogo(organizationDetails.logo_url || "");
      dataLoadedRef.current = true;
    }
  }, [selectedOrganization, organizationDetails, isSuperAdmin]);

  const logoUrlWithCacheBusting = orgLogo ? `${orgLogo}${orgLogo.includes('?') ? '&' : '?'}v=${logoUpdateCounter}` : '';

  // Reflete edição da organização (nome/logo) sem recarregar a página. Só vale
  // quando os detalhes carregados SÃO os da organização ativa: eles vêm da
  // organização do perfil, então após uma troca escreveriam o nome errado no card.
  useEffect(() => {
    if (isSuperAdmin) return;
    if (!organizationDetails || !dataLoadedRef.current) return;
    if (selectedOrganization && selectedOrganization.id !== organizationDetails.id) return;

    setOrgName(organizationDetails.name || "");
    setOrgLogo(organizationDetails.logo_url || "");
    setLogoUpdateCounter(prev => prev + 1);
  }, [organizationDetails, isSuperAdmin, selectedOrganization]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLogoUpdateCounter(prev => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const menuItems: MenuItem[] = useMemo(() => [
    { icon: BarChart3, label: t("dashboards.title"), href: "/", scopes: ['platform', 'organization', 'product'], tourId: "sidebar-dashboards" },
    { icon: Network, label: t("navigation.integrations"), href: "/integrations", scopes: ['organization', 'product'] },
    { icon: BrainCircuit, label: t("navigation.knowledge"), href: "/knowledge", scopes: ['product'] },
    { icon: Bot, label: t("navigation.agents"), href: "/agents", scopes: ['product'] },
    { icon: PlayCircle, label: t("navigation.playground"), href: "/playground", scopes: ['product'] },
    { icon: Terminal, label: t("settings.tabs.logs"), href: "/admin/logs", scopes: ['platform'] },
    { icon: Bot, label: t("settings.tabs.queues", "Queues"), href: "/admin/queues", scopes: ['platform'] },
    { icon: LayoutGrid, label: t("settings.tabs.templates", "Templates"), href: "/admin/templates", scopes: ['platform'] },
    { icon: MessageSquare, label: t("settings.tabs.feedback"), href: "/admin/feedback", scopes: ['platform'] },
    { icon: Mail, label: t("settings.sidebar.email"), href: "/admin/email", scopes: ['platform'] },
    { icon: CreditCard, label: t("settings.tabs.plans", "Plans"), href: "/admin/plans", scopes: ['platform'] },
  ], [t]);

  const visibleMenuItems = useMemo(() => {
    // user role: só vê menu quando há produto selecionado (sem visão da organização)
    if (isUserOnly && scope !== 'product') return [];
    return menuItems.filter(item => {
      if (!item.scopes.includes(scope)) return false;
      // items de platform scope são exclusivos de superadmin (evita flash em login de admin/user
      // antes do organization_id chegar do useOrganization)
      if (scope === 'platform' && !isSuperAdmin) return false;
      return true;
    });
  }, [menuItems, scope, isUserOnly, isSuperAdmin]);

  const handleNavigate = (href: string) => {
    navigate(href);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  // Show org card whenever we have data to show OR we are superadmin (empty state handled by switcher)
  const showOrgCard = isSuperAdmin || (!!orgName && dataLoadedRef.current);

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-30 lg:hidden transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      <aside
        className={cn(
          "fixed left-0 top-16 bottom-0 z-40 glass-effect w-[280px] lg:w-64 transition-transform duration-300 flex flex-col",
          !isOpen && "-translate-x-full"
        )}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 lg:hidden"
        >
          <X className="h-4 w-4" />
        </button>

        <ScrollArea className="flex-grow py-6 relative">
          <div className="space-y-4 py-4">
            <div className="px-3 py-2">
              <div className="space-y-1">
                {visibleMenuItems.map((item) => {
                  const isActive = location.pathname === item.href;

                  return (
                    <Button
                      key={item.href}
                      // Sempre `ghost`: o item ativo indica ONDE VOCÊ ESTÁ, não
                      // uma ação. Usar a variante primária aqui gastava o token
                      // de maior ênfase do sistema em navegação e deixava o item
                      // do menu com mais peso visual que os CTAs de verdade.
                      variant="ghost"
                      className={cn(
                        "relative w-full justify-start font-normal",
                        // Barra da marca à esquerda: o azul entra como marcador,
                        // em área mínima, em vez de preencher o item.
                        isActive &&
                          "bg-active text-foreground before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-full before:bg-primary hover:bg-active"
                      )}
                      onClick={() => handleNavigate(item.href)}
                    >
                      <item.icon className="mr-2 h-4 w-4" />
                      {item.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="px-4 pb-4">
          <Separator className="mb-4" />
          {/* Âncora do aviso: envolve SÓ o card, para ele encostar no card e não no
              separador — o alvo do recado é o card. */}
          <div className="relative">
            <OrgSwitcherHint sidebarOpen={isOpen} />
            <div
              data-org-switcher-card
              className={cn(
              "relative rounded-r-xl shadow-sm bg-transparent transition-all duration-500 ease-in-out",
              "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-black",
              showOrgCard
                ? "opacity-100 border border-border p-4 translate-y-0"
                : "opacity-0 invisible h-0 p-0 overflow-hidden border-0 translate-y-2 before:hidden"
            )}>
              {showOrgCard && canSwitchOrganization ? (
                <SuperAdminOrgSwitcher
                  orgName={orgName}
                  orgLogo={orgLogo}
                  logoUrl={logoUrlWithCacheBusting}
                  planName={planName}
                  subStatus={subStatus}
                  statusLabel={statusLabel}
                  statusColor={statusColor}
                  onLogoError={() => setOrgLogo(FALLBACK_ORG.logo_url || "")}
                  onNavigateSettings={() => handleNavigate("/settings")}
                />
              ) : showOrgCard ? (
                <div className="flex items-center gap-3 animate-fadeIn">
                  <div className="relative h-12 w-12 rounded-lg overflow-hidden shadow-sm">
                    {orgLogo ? (
                      <img
                        src={logoUrlWithCacheBusting}
                        alt={orgName}
                        className="object-contain w-full h-full bg-secondary p-1"
                        onError={() => {
                          setOrgLogo(FALLBACK_ORG.logo_url || "");
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground">
                        {orgName?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="text-sm font-medium truncate mb-0.5">
                      {orgName}
                    </p>
                    {planName && (
                      <p className="text-xs truncate">
                        <span className="font-medium text-foreground underline underline-offset-2">{planName}</span>
                        {subStatus && planName?.toLowerCase() !== subStatus && <span className={cn("ml-1.5", statusColor[subStatus] || "text-muted-foreground")}>· {statusLabel[subStatus] || subStatus}</span>}
                      </p>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-full h-8 w-8 hover:bg-blue-100/50 dark:hover:bg-blue-900/50 transition-colors duration-200"
                      onClick={() => handleNavigate("/settings")}
                      title={t("navigation.settings")}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};
